import { router } from "@inertiajs/react"
import { Check, MailPlus, Sparkles } from "lucide-react"
import type { ComponentType, SVGProps } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"

/**
 * OKS-21 / OKS-673 — Empty state for the Invites panel. This is the
 * candidate green-flow driver: it renders exactly ONE primary state at
 * a time, chosen by which BE-supplied URL is non-null.
 *
 * Branches by `mode` + advance-URL nullability:
 *   - `"approved"` — candidate is `approved`/`invited` with no active
 *     invite. Renders the Generate CTA (posts empty body to
 *     `generate_url`; OKS-21 behaviour, unchanged).
 *   - `"not_approved"` — pre-approval. OKS-673 makes this interactive:
 *       · `markReviewedUrl` non-null → inline **Mark as reviewed**
 *         (`new` candidate).
 *       · else `approveUrl` non-null → inline **Approve** (`reviewed`
 *         candidate) — replaces the old passive "Approve first"
 *         placeholder.
 *       · else → guidance copy, NO button (rejected / blocklisted /
 *         viewer not allowed to advance).
 *
 * **Same gate, no widening.** The advance URLs come straight from the
 * candidate `actions` surface: `null` = state-illegal OR role-locked
 * (BE decides). FE never re-derives from `candidate.status`. A viewer
 * who cannot advance falls through to guidance copy — never a
 * dead/disabled CTA implying a forbidden action.
 *
 * **Wire format.** Advance buttons post the SAME nested strong-params
 * shape the action bar used: `router.post(url, {transition: {event}})`.
 * Testids move verbatim from the action bar
 * (`candidate-detail-mark-reviewed` / `candidate-detail-approve`) so
 * location-agnostic Playwright/request selectors keep working.
 *
 * Backend 303-redirects to candidate detail with refreshed `actions` +
 * `invite` props, so the section re-renders into the next state
 * automatically (new → Approve → Generate link). `flash[:success]`
 * toasts via `useFlash` (mounted once at `PersistentLayout`).
 */

/**
 * Advance-event → button metadata. Pre-approval green-flow steps only
 * (`mark_reviewed`, `approve`). Icon mirrors the action bar
 * (Sparkles for review, Check for approve) so the relocated buttons
 * read identically to their old bar home.
 */
type AdvanceEvent = "mark_reviewed" | "approve"

interface AdvanceActionDef {
  url: string
  event: AdvanceEvent
  testId: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  /** Button label — reuses the frozen action i18n keys. */
  labelKey: string
  /** Section heading key (new microcopy, OKS-673). */
  headingKey: string
  /** One-line helper key (new microcopy, OKS-673). */
  helperKey: string
}

interface InvitesPanelEmptyProps {
  mode: "approved" | "not_approved"
  /** POST URL — non-null when `mode === "approved"` (per
   *  `invite_actions.generate_url` contract). */
  generateUrl: string | null
  /**
   * OKS-673 — transition URL for a `new` candidate (inline Mark as
   * reviewed). Non-null iff BE serialized it for this viewer/brand.
   */
  markReviewedUrl?: string | null
  /**
   * OKS-673 — transition URL for a `reviewed` candidate (inline
   * Approve). Non-null iff BE serialized it for this viewer/brand.
   */
  approveUrl?: string | null
  /** Candidate handle for the localised description copy. */
  handle: string
  className?: string
}

export function InvitesPanelEmpty({
  mode,
  generateUrl,
  markReviewedUrl,
  approveUrl,
  handle,
  className,
}: InvitesPanelEmptyProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [submitting, setSubmitting] = useState(false)

  if (mode === "not_approved") {
    // OKS-673 — pick the single available advance action. mark_reviewed
    // takes precedence over approve (they're mutually exclusive per the
    // state machine, but the order encodes new → reviewed intent).
    const advance: AdvanceActionDef | null = markReviewedUrl
      ? {
          url: markReviewedUrl,
          event: "mark_reviewed",
          testId: "candidate-detail-mark-reviewed",
          icon: Sparkles,
          labelKey: "candidates.detail.actions.mark_reviewed",
          headingKey: "invites.panel.advance.new.heading",
          helperKey: "invites.panel.advance.new.helper",
        }
      : approveUrl
        ? {
            url: approveUrl,
            event: "approve",
            testId: "candidate-detail-approve",
            icon: Check,
            labelKey: "candidates.detail.actions.approve",
            headingKey: "invites.panel.advance.reviewed.heading",
            helperKey: "invites.panel.advance.reviewed.helper",
          }
        : null

    if (advance) {
      const Icon = advance.icon
      const disabled = submitting || !online
      const handleAdvance = () => {
        if (disabled) return
        setSubmitting(true)
        // Same nested strong-params wire as the action bar's forward
        // POST — `params.require(:transition).permit(:event, :reason)`.
        router.post(
          advance.url,
          { transition: { event: advance.event } },
          {
            preserveScroll: true,
            onFinish: () => setSubmitting(false),
          },
        )
      }

      return (
        <div
          data-testid="invites-panel-empty"
          data-mode="not_approved"
          className={cn(
            "border-border flex flex-col items-center gap-4 rounded-lg border border-dashed px-6 py-8 text-center",
            className,
          )}
        >
          <Icon className="text-muted-foreground size-8" aria-hidden="true" />
          <div className="flex flex-col gap-1">
            <h3 className="text-foreground font-medium">
              {t(advance.headingKey)}
            </h3>
            <p className="text-muted-foreground text-sm">
              {t(advance.helperKey)}
            </p>
          </div>
          <Button
            type="button"
            onClick={handleAdvance}
            disabled={disabled}
            aria-disabled={disabled}
            data-testid={advance.testId}
            className="w-full sm:w-auto"
          >
            <Icon className="size-4" aria-hidden="true" />
            {t(advance.labelKey)}
          </Button>
        </div>
      )
    }

    // Fall-through — no available advance action (rejected /
    // blocklisted / viewer not allowed). Guidance copy, no button.
    return (
      <div
        data-testid="invites-panel-empty"
        data-mode="not_approved"
        className={cn(
          "border-border flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-8 text-center",
          className,
        )}
      >
        <h3 className="text-foreground font-medium">
          {t("invites.panel.not_approved.heading")}
        </h3>
        <p className="text-muted-foreground text-sm">
          {t("invites.panel.not_approved.description")}
        </p>
      </div>
    )
  }

  const handleGenerate = () => {
    if (!generateUrl || submitting || !online) return
    setSubmitting(true)
    router.post(
      generateUrl,
      {},
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <div
      data-testid="invites-panel-empty"
      data-mode="approved"
      className={cn(
        "border-border flex flex-col items-center gap-4 rounded-lg border border-dashed px-6 py-8 text-center",
        className,
      )}
    >
      <MailPlus className="text-muted-foreground size-8" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <h3 className="text-foreground font-medium">
          {t("invites.panel.empty.heading")}
        </h3>
        <p className="text-muted-foreground text-sm">
          {t("invites.panel.empty.description", { handle })}
        </p>
      </div>
      {generateUrl && (
        <Button
          type="button"
          onClick={handleGenerate}
          disabled={submitting || !online}
          data-testid="invites-panel-generate-button"
        >
          {t("invites.panel.empty.cta")}
        </Button>
      )}
    </div>
  )
}
